import { EyeOutlined } from '@ant-design/icons'
import { Modal, Tag } from 'antd'
import Table, { ColumnsType } from 'antd/es/table'
import { GeckoMeasurementLabel } from 'features/fde'
import { CriticalProfilePoint } from 'features/indicationTriage/cards/criticalProfile/criticalProfileApi'
import { WORKING_UNIT_THICKNESS } from 'features/indicationTriage/cards/ffsCalc/ffsUnits'
import { computeMtCylindrical } from 'features/indicationTriage/cards/ffsCalc/foliasProfileLookup'
import { computeLambda } from 'features/indicationTriage/cards/ffsCalc/levelOne/FfsDataValuesSection'
import { useGeckoLocale } from 'features/locale'
import _ from 'lodash'
import React, { FC, ReactNode, useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import { GeckoMeasurement, GeckoMeasurementUnit } from 'types'

export interface CtpSlice {
  si_s: GeckoMeasurement
  si_e: GeckoMeasurement
  s: GeckoMeasurement
  lambda: GeckoMeasurement
  mt: GeckoMeasurement
  ai: number
  ai0: number
  rsf: GeckoMeasurement
}

export type Rsf = {
  startIdx: number
  rsf: GeckoMeasurement | undefined
  slices: ReadonlyArray<CtpSlice>
}

interface ComputeSlicesArgs {
  criticalPath: ReadonlyArray<CriticalProfilePoint>
  startIdx: number
  tc: GeckoMeasurement
  nominal: GeckoMeasurement
  axis: 'x' | 'y'
  fca: GeckoMeasurement
  innerDiameter: GeckoMeasurement
}

function computeSegmentArea(
  x1: number,
  y1: number,
  x2: number,
  y2: number
): number {
  const triangleHeight = Math.abs(y2 - y1)
  const triangleWidth = Math.abs(x2 - x1)
  const rectHeight = Math.min(y1, y2)
  const triangleArea = (triangleHeight * triangleWidth) / 2
  const rectArea = rectHeight * triangleWidth
  return triangleArea + rectArea
}

function renderMeas(meas: GeckoMeasurement | undefined): ReactNode {
  return meas ? (
    <GeckoMeasurementLabel variant='narrow' measurement={meas} />
  ) : (
    <></>
  )
}

function computeSlices({
  criticalPath,
  startIdx,
  tc,
  nominal,
  axis,
  fca,
  innerDiameter,
}: ComputeSlicesArgs): ReadonlyArray<CtpSlice> {
  return _.uniqBy(
    _.range(1, criticalPath.length).map((i) => [
      Math.max(startIdx - i, 0),
      Math.min(startIdx + i, criticalPath.length - 1),
    ]),
    ([minIdx, maxIdx]) => `${minIdx}:${maxIdx}`
  ).map(([sliceMinIdx, sliceMaxIdx]) => {
    const slicePoints = _.slice(criticalPath, sliceMinIdx, sliceMaxIdx)

    const pathMin = _.first(criticalPath)!.coords[axis]
    const [si_s, si_e] = [_.first(slicePoints)!, _.last(slicePoints)!]
      .map((point) => point.coords[axis])
      .map((s) => s.minus(pathMin))
    const s = si_e.minus(si_s)

    const lambda = computeLambda(innerDiameter, s, tc)
    const mt = GeckoMeasurement.CreateFromValue(
      computeMtCylindrical(lambda.toNumber(GeckoMeasurementUnit.Ratio)),
      GeckoMeasurementUnit.Ratio
    )

    const segmentIdxs =
      slicePoints.length > 1 ? _.range(1, slicePoints.length) : []
    const metalArea = _.sum(
      segmentIdxs.map((segI) => {
        const [prev, curr] = [slicePoints[segI - 1], slicePoints[segI]]
        const [prevValue, currValue] = [prev, curr].map((crit) => crit.value)
        if (_.isNil(prevValue) || _.isNil(currValue)) {
          console.warn()
          return 0
        }
        return computeSegmentArea(
          prev.coords[axis].toNumber(WORKING_UNIT_THICKNESS),
          prevValue.minus(fca).toNumber(WORKING_UNIT_THICKNESS),
          curr.coords[axis].toNumber(WORKING_UNIT_THICKNESS),
          currValue.minus(fca).toNumber(WORKING_UNIT_THICKNESS)
        )
      })
    )

    const ai0 =
      s.toNumber(WORKING_UNIT_THICKNESS) *
      nominal.toNumber(WORKING_UNIT_THICKNESS)
    const ai = ai0 - metalArea
    const rsfNumerator = 1 - ai / ai0
    const rsfDenominator =
      1 - (1 / mt.toNumber(GeckoMeasurementUnit.Ratio)) * (ai / ai0)
    const rsf = GeckoMeasurement.CreateFromValue(
      rsfNumerator / rsfDenominator,
      GeckoMeasurementUnit.Ratio
    )
    return {
      si_s,
      si_e,
      lambda,
      mt,
      ai,
      ai0,
      rsf,
      s,
    }
  })
}

const CtpSliceTable: FC<{ slices: ReadonlyArray<CtpSlice> }> = ({ slices }) => {
  const columns: ColumnsType<CtpSlice> = useMemo(
    () => [
      {
        key: 'si_s',
        dataIndex: 'si_s',
        title: (
          <>
            Si<sub>s</sub>
          </>
        ),
        render: renderMeas,
      },
      {
        key: 'si_e',
        dataIndex: 'si_e',
        title: (
          <>
            Si<sub>e</sub>
          </>
        ),
        render: renderMeas,
      },
      {
        key: 's',
        dataIndex: 's',
        title: 's',
        render: renderMeas,
      },
      {
        key: 'lambda',
        dataIndex: 'lambda',
        title: 'λ',
        render: renderMeas,
      },
      {
        key: 'mt',
        dataIndex: 'mt',
        title: (
          <>
            M<sub>t</sub>
          </>
        ),
        render: renderMeas,
      },
      {
        key: 'ai0',
        dataIndex: 'ai0',
        title: (
          <>
            A<sub>i0</sub>
          </>
        ),
        render: (val) => <AreaLabel area={val} />,
      },
      {
        key: 'ai',
        dataIndex: 'ai',
        title: (
          <>
            A<sub>i</sub>
          </>
        ),
        render: (val) => <AreaLabel area={val} />,
      },
      {
        key: 'rsf',
        dataIndex: 'rsf',
        title: (
          <>
            RSF<sub>i</sub>
          </>
        ),
        render: renderMeas,
      },
    ],
    []
  )

  return (
    <Table
      style={{ width: '1000px' }}
      columns={columns}
      dataSource={slices}
      rowKey={({ si_s, si_e }) => [si_s, si_e].join('::')}
      pagination={false}
    />
  )
}

type ComputeRsfsArgs = Pick<
  ComputeSlicesArgs,
  'criticalPath' | 'tc' | 'nominal' | 'axis' | 'fca' | 'innerDiameter'
>
export function computeRsfs({
  criticalPath,
  tc,
  nominal,
  axis,
  fca,
  innerDiameter,
}: ComputeRsfsArgs): ReadonlyArray<{
  startIdx: number
  rsf: GeckoMeasurement | undefined
  slices: ReadonlyArray<CtpSlice>
}> {
  const rankedStartingPoints = _.sortBy(
    criticalPath.filter((point) => !!point.value),
    (point) => point.value!.toNumber(WORKING_UNIT_THICKNESS)
  )
  const startIdxs = rankedStartingPoints.map((startingPoint) =>
    _.indexOf(criticalPath, startingPoint)
  )

  return startIdxs.map((startIdx) => {
    const slices = computeSlices({
      criticalPath,
      startIdx,
      tc,
      nominal,
      axis,
      fca,
      innerDiameter,
    })
    const rsf = _.minBy(
      slices.map((slice) => slice.rsf),
      (rsf) => rsf.toNumber(GeckoMeasurementUnit.Ratio)
    )
    return {
      slices,
      rsf,
      startIdx,
    }
  })
}

const AreaLabel: FC<{ area: number }> = ({ area }) => {
  const pref1DUnit = useGeckoLocale().getPreferredMeasurementUnit(
    WORKING_UNIT_THICKNESS
  )
  const convertedNum = Math.pow(
    GeckoMeasurement.CreateFromValue(
      Math.sqrt(area),
      WORKING_UNIT_THICKNESS
    ).toNumber(pref1DUnit),
    2
  )
  return (
    <>
      {convertedNum.toPrecision(3)}
      {pref1DUnit.name}
      <sup>2</sup>
    </>
  )
}

export interface RsfTableProps {
  rsfs: ReadonlyArray<Rsf>
}

const ControlsContainer = styled.div`
  display: flex;
  justify-content: center;
  min-width: 50px;
`

export const RsfTable: FC<{ rsfs: ReadonlyArray<Rsf> }> = ({ rsfs }) => {
  const [selectedRsfStartIdx, setSelectedRsfStartIdx] = useState<
    number | undefined
  >(undefined)
  const columns = useMemo<ColumnsType<Rsf & { rank: number }>>(() => {
    return [
      {
        key: 'rank',
        title: 'Rank',
        dataIndex: 'rank',
      },
      {
        key: 'start_reading',
        title: 'Start Slice',
        dataIndex: 'startIdx',
      },
      {
        key: 'rsf',
        title: 'RSF',
        dataIndex: 'rsf',
        render: renderMeas,
      },
      {
        key: 'controls',
        render: (val, row) => {
          return (
            <ControlsContainer>
              <Tag.CheckableTag
                checked={row.startIdx === selectedRsfStartIdx}
                onChange={(checked) =>
                  setSelectedRsfStartIdx(checked ? row.startIdx : undefined)
                }>
                <EyeOutlined />
              </Tag.CheckableTag>
            </ControlsContainer>
          )
        },
      },
    ]
  }, [selectedRsfStartIdx])

  const rankedRsfs = useMemo(
    () => rsfs.map((rsf, idx) => ({ ...rsf, rank: idx + 1 })),
    [rsfs]
  )

  const selectedRsfSlices = useMemo(
    () =>
      rsfs.find((rsf) => rsf.startIdx === selectedRsfStartIdx)?.slices ?? [],
    [rsfs, selectedRsfStartIdx]
  )
  const closeModal = useCallback(() => {
    setSelectedRsfStartIdx(undefined)
  }, [])

  return (
    <>
      <Modal
        open={!!selectedRsfStartIdx}
        footer={null}
        onClose={closeModal}
        onCancel={closeModal}
        title={`Slices for RSF ${selectedRsfStartIdx}`}
        width='1000px'>
        <CtpSliceTable slices={selectedRsfSlices} />
      </Modal>
      <Table
        columns={columns}
        dataSource={rankedRsfs}
        rowKey={(rsfRec) => rsfRec.startIdx}
      />
    </>
  )
}
